import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SignOut24Regular } from '@fluentui/react-icons';
import { useMutation } from '@tanstack/react-query';
import { useApi } from '../auth/useApi.ts';
import { useI18n } from '../i18n/context.tsx';
import type { MeResponse } from '../auth/useMe.ts';

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    background: tokens.colorNeutralBackground2,
    padding: '24px',
  },
  card: {
    background: tokens.colorNeutralBackground1,
    padding: '40px 48px',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow28,
    maxWidth: '520px',
    display: 'grid',
    gap: '20px',
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
  },
  body: { lineHeight: tokens.lineHeightBase300 },
  buttonRow: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
});

/**
 * Shown when a signed-in user's status is `pending` or `denied`. Pending users
 * get a "send request" CTA; denied users just see a terminal message and sign-out.
 */
export function PendingApprovalScreen({ me }: { me: MeResponse }) {
  const styles = useStyles();
  const { request } = useApi();
  const { instance } = useMsal();
  const { t } = useI18n();
  const [submitted, setSubmitted] = useState(false);

  const sendRequest = useMutation({
    mutationFn: () => request('/admin/join-request', { method: 'POST' }),
    onSuccess: () => setSubmitted(true),
  });

  const isDenied = me.status === 'denied';

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <Text className={styles.title}>
          {isDenied ? t('users', 'deniedTitle') : t('users', 'pendingTitle')}
        </Text>
        <Text className={styles.body}>
          {isDenied
            ? t('users', 'deniedBody', { email: me.email })
            : t('users', 'pendingBody', { email: me.email })}
        </Text>

        {submitted && !isDenied ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>{t('users', 'requestSent')}</MessageBarTitle>
            </MessageBarBody>
          </MessageBar>
        ) : null}

        {sendRequest.isError ? (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{t('common', 'saveFailed')}</MessageBarTitle>
              {(sendRequest.error as Error)?.message}
            </MessageBarBody>
          </MessageBar>
        ) : null}

        <div className={styles.buttonRow}>
          <Button
            appearance="secondary"
            icon={<SignOut24Regular />}
            onClick={() => instance.logoutRedirect().catch(console.error)}
          >
            {t('common', 'signOut')}
          </Button>
          {!isDenied ? (
            <Button
              appearance="primary"
              disabled={submitted || sendRequest.isPending}
              onClick={() => sendRequest.mutate()}
            >
              {sendRequest.isPending ? <Spinner size="tiny" /> : t('users', 'sendRequest')}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
